import { useCallback, useEffect, useState, useReducer, useRef, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Grid,
    TextField,
    Chip,
    IconButton,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Divider,
    InputLabel,
    Stack,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import { Upload, Eye, Users, Lock, Globe, Save } from 'lucide-react';
import { Add, Close } from '@mui/icons-material';
import { EsBackButton } from '../EsButtons/EsBackButton';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CustomizedSwitches } from '../Switch/Switch';
import { MainTagCarousel } from '../EsCarousels/MainTagCarousel';
import { EmptySection } from '../EsCards/EmptySection';
import { ThspianFullLogo } from '../ThspianLogo/ThspianFullLogo';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { useLogoHomeNavigation } from '../../hook/useLogoHomeNavigation';
import { useParams, useSearchParams } from 'react-router-dom';
import { toggleSubscriptionModal } from '../../redux/appConfigurator/action';
import { toggleAccountSubscriptionModalSelector } from '../../selectors/selectors';
import { AccountSubscriptionModal } from '../../pages/AccountSettings/AccountSubscriptionModal';
import {
    clearPostAutomationIntent,
    getPostAutomationDraft,
    shouldAutoPublishPostAutomation,
    shouldOpenPostAutomationForProject,
} from '../../constants/postAutomationIntent';
import {
    getManageProject,
    updateProject,
    publishProject,
    deleteProjectCredit,
    addProjectCredit,
    updateProjectFile,
} from '../../redux/project/actions';
import { managedProjectSelector } from '../../pages/Feed/selector';
import { NebulaPlayer } from '../VideoPlayer/NebulaPlayer/NebulaPlayer';
import { IProject } from '../../types/global/helper';
import { Loading } from '../Loading/Loading';
import { formatNumberWithK, generateShareLink } from '../../utils/AppUtils';
import PictureUploader from '../PictureUploader/PictureUploader';
import { PROJECT_CATEGORY } from '../../data/forms/project.data';
import { LANGUAGES } from '../FindTalentsContainer/filterEnums';
import { isEqual } from 'lodash';
import Compressor from 'compressorjs';
import { Credit } from '../../types/global/manageProject.types';
import { searchUser } from '../../redux/user/actions';
import { userSearchSelector, searchingUserSearchSelector } from '../AvatarGroup/selector';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import _ from 'lodash';
import { IUserSearchResult, IWorkProfileSummary } from '../../redux/user/types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { CREATIVE_ROLES } from '../../data/forms/creativeRoles.data';
import { isEmpty } from '../../utils/lodash';
import { ROUTES } from '../../types/global/routes.types';
import { isProjectProcessingSelector, mediaForgeMessageSelector } from '../../selectors/selectors';
import { pauseUpload, resumeUpload, terminateUplaod } from '../../redux/upload/action';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { useMediaUploader } from '../../hook/useMediaUploader';
import { Spinner } from '@chakra-ui/react';
import { AppWarning } from '../AppWarning/AppWarning';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import dayjs from 'dayjs';
import { connect } from '../../redux/live/action';
import { LinkBox } from '../LinkBox';
import { WorkshopInstagramAutomationCard } from '../InstagramAutomation/WorkshopInstagramAutomationCard';
import { InstagramPostAutomationModal } from '../InstagramAutomation/InstagramPostAutomationModal';
import { resolveProjectStandaloneMedia } from '../InstagramAutomation/resolveProjectStandaloneMedia';

// Type for user selection that can handle both existing users and email entries
type UserOption = IUserSearchResult & { selectedProfile?: IWorkProfileSummary };

// Initial state for the form
const initialState = {
    title: '',
    description: '',
    tag: [] as string[],
    project_type: '',
    language: 'english',
    category: '',
    public_status: 'public' as 'public' | 'unlisted' | 'private',
    disable_comments: false,
    downloadable: true,
    scheduled_published_date: null as Date | null,
    feature_image: null as any,
    feature_image_preview: '',
    credits: [] as Credit[],
    isEditMode: false,
    scheduledPublishModified: false, // Track if scheduled publish has been modified
};

type Action =
    | { type: 'FIELD'; field: string; value: any }
    | { type: 'SET_EDIT_MODE'; value: boolean }
    | { type: 'RESET'; payload: any };

const reducer = (state: typeof initialState, action: Action) => {
    switch (action.type) {
        case 'FIELD':
            return {
                ...state,
                [action.field]: action.value,
            };
        case 'SET_EDIT_MODE':
            return {
                ...state,
                isEditMode: action.value,
            };
        case 'RESET':
            return {
                ...initialState,
                ...action.payload,
            };
        default:
            return state;
    }
};

const WorkShopComponent = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [newTag, setNewTag] = useState<string>('');
    const [error, setError] = useState(false);
    const [tab, setTab] = useState<string>('thumbnail');
    const [showAddCredit, setShowAddCredit] = useState(false);
    const [deleteCreditIndex, setDeleteCreditIndex] = useState<number | null>(null);
    const { themeStyle, device } = useThemeContext();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { projectId } = params;
    const reduxDispatch = useDispatch();
    const { loading, project, replacingInProgress } = useSelector(managedProjectSelector);
    const [tempPreview, setTempPreview] = useState<string>('');
    const [, setTempImagePreview] = useState<File>();
    const initialValues = useRef(project);
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);
    const isProjectProcessing = useSelector(isProjectProcessingSelector);
    const { progress, uploadMessage, isPaused } = useSelector(mediaForgeMessageSelector);
    const isPublished = project?.status === 'published';
    const [automationModalOpen, setAutomationModalOpen] = useState(false);
    const [igAutomationActive, setIgAutomationActive] = useState(false);
    const subscriptionModalOpen = useSelector(toggleAccountSubscriptionModalSelector);

    const standaloneMedia = useMemo(() => resolveProjectStandaloneMedia(project as IProject | undefined), [project]);
    const automationAutoOpenedRef = useRef(false);

    // Create-project "Post Automation": open on workshop even while upload runs; modal shows progress.
    useEffect(() => {
        if (automationAutoOpenedRef.current) return;
        const wantsAutomation =
            searchParams.get('openAutomation') === '1' || shouldOpenPostAutomationForProject(projectId);
        if (!wantsAutomation) return;
        if (!projectId || !project) return;

        automationAutoOpenedRef.current = true;
        setAutomationModalOpen(true);
        if (searchParams.get('openAutomation') === '1') {
            const next = new URLSearchParams(searchParams);
            next.delete('openAutomation');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams, projectId, project]);

    useEffect(() => {
        if (standaloneMedia?.media_url) {
            clearPostAutomationIntent();
        }
    }, [standaloneMedia?.media_url]);

    const { openFilePicker, loading: uploadLoading } = useMediaUploader({
        onFileParsed: ({ file, preview, featureImageFile, metadata }) => {
            handleUpdateProjectFile(metadata, file);
            setTempPreview(preview);
            setTempImagePreview(featureImageFile);
        },
        accept: ['video/*'],
    });

    const addTag = (): void => {
        if (newTag.trim() && !state.tag.includes(newTag.trim())) {
            dispatch({ type: 'FIELD', field: 'tag', value: [...state.tag, newTag.trim()] });
            setNewTag('');
        }
    };

    const renderPlayer = useMemo(() => {
        return (
            <NebulaPlayer
                url={project?.project as string}
                project={project as unknown as IProject}
                autoPlay={false}
                endlessPlayer={false}
                customPoster={tempPreview}
                persistInline
                useProjectUrl={false}
                onLoadFailure={() => {
                    setError(!error);
                }}
                isEditMode
                videoStyle={{
                    height: 'fit-content', // Fixed height to match the overlay
                    width: 'auto',
                }}
            />
        );
    }, [error, project, tempPreview]);

    const handleUpdateProjectFile = (metadata: any, file: File) => {
        if (projectId) {
            reduxDispatch(
                updateProjectFile({
                    id: projectId,
                    metadata: metadata,
                    project: file,
                }),
            );
        }
    };

    const handleGoBack = () => {
        reduxDispatch(
            navigate({
                routes: ROUTES.ESMY_VIDEOS,
            }),
        );
    };

    const handleGoHome = () => {
        reduxDispatch(
            navigate({
                routes: ROUTES.FEED,
            }),
        );
    };

    const { handleLogoClick } = useLogoHomeNavigation({
        onDefaultNavigate: handleGoHome,
    });

    const removeTag = (tagToRemove: string): void => {
        dispatch({ type: 'FIELD', field: 'tag', value: state.tag.filter((tag: string) => tag !== tagToRemove) });
    };

    const addCredit = (): void => {
        dispatch({
            type: 'FIELD',
            field: 'credits',
            value: [...state.credits, { _id: '', credit_role: '', credit_type: 'user', user: {} as UserOption }],
        });
    };

    const updateCredit = (index: number, field: 'credit_role' | 'user', value: string | UserOption): void => {
        const updatedCredits = [...state.credits];
        const updatedCredit = { ...updatedCredits[index], [field]: value };
        updatedCredits[index] = updatedCredit;

        dispatch({ type: 'FIELD', field: 'credits', value: updatedCredits });
    };

    const removeCredit = (index: number): void => {
        dispatch({
            type: 'FIELD',
            field: 'credits',
            value: state.credits.filter((_: Credit, i: number) => i !== index),
        });
    };

    const handleRemoveCredit = (index: number): void => {
        const credit = state.credits[index];
        // Check if this is a saved credit (has an _id) or an unsaved credit (no _id)
        if (credit._id) {
            // This is a saved credit, show confirmation dialog
            setDeleteCreditIndex(index);
        } else {
            // This is an unsaved credit, remove it immediately
            removeCredit(index);
            // Hide the form when removing unsaved credit
            setShowAddCredit(false);
        }
    };

    const handleSaveCredit = (): void => {
        const updatedCredits = [...state.credits];
        const lastCreditIndex = updatedCredits.length - 1;
        const currentCredit = updatedCredits[lastCreditIndex];

        if (currentCredit && !currentCredit._id) {
            // Determine credit_type based on selected profile
            const credit_type =
                currentCredit.credit_role.toLowerCase() === 'actor' ||
                currentCredit.credit_role.toLowerCase() === 'actress'
                    ? 'cast'
                    : 'crew';

            // Create payload for API call
            const payload = {
                project: project?._id || '',
                user: currentCredit.user?._id || '',
                ...(currentCredit.user?.selectedProfile?._id && { profile: currentCredit.user.selectedProfile._id }),
                credit_role: currentCredit.credit_role || '',
                credit_type: credit_type,
            };

            // Call addCredit API
            reduxDispatch(addProjectCredit(payload));

            // Mark the credit as saved by giving it a temporary _id
            currentCredit._id = `temp_${Date.now()}`; // Temporary ID for saved credits
            dispatch({ type: 'FIELD', field: 'credits', value: updatedCredits });
        }
        setShowAddCredit(false);
    };

    const handleDeleteCredit = (): void => {
        if (deleteCreditIndex !== null) {
            const creditToDelete = state.credits[deleteCreditIndex];

            // Create payload for API call if it's a saved credit
            if (creditToDelete._id && !creditToDelete._id.startsWith('temp_')) {
                const payload = {
                    documentId: creditToDelete._id,
                };

                console.log('Delete credit payload:', payload);
                // Call deleteCredit API
                reduxDispatch(deleteProjectCredit(payload));
            }

            removeCredit(deleteCreditIndex);
            setDeleteCreditIndex(null);
        }
    };

    // Helper function to check if there are any unsaved credits
    const hasUnsavedCredits = (): boolean => {
        return state && state.credits && state.credits.some((credit: Credit) => !credit._id);
    };

    const handleFormUpdate = (field: string, value: any, shouldSave?: boolean) => {
        dispatch({ type: 'FIELD', field, value });

        // Always check if the field has changed from initial values
        if (initialValues.current && !isEqual((initialValues.current as any)[field], value)) {
            dispatch({ type: 'SET_EDIT_MODE', value: true });
        } else if (initialValues.current && isEqual((initialValues.current as any)[field], value)) {
            // Check if any other fields have changed
            const hasOtherChanges = Object.keys(state).some(key => {
                if (key === field || key === 'isEditMode' || key === 'scheduledPublishModified') return false;
                return !isEqual((initialValues.current as any)[key], (state as any)[key]);
            });

            if (!hasOtherChanges) {
                dispatch({ type: 'SET_EDIT_MODE', value: false });
            }
        }
    };

    const handleImageCompressAndDispatch = (
        image: string | any,
        rawFile?: File[] | FileList | null,
        alternateKey?: string,
    ) => {
        if (rawFile && Array.isArray(rawFile) && rawFile.length > 0) {
            const file = rawFile[0];
            new Compressor(file, {
                quality: 0.6,
                success: compressedResult => {
                    const url = URL.createObjectURL(compressedResult);
                    handleFormUpdate('feature_image', compressedResult);
                    handleFormUpdate('feature_image_preview', url);
                },
            });
        } else {
            handleFormUpdate('feature_image', null);
            handleFormUpdate('feature_image_preview', '');
        }
    };

    const handleSave = () => {
        if (state.isEditMode && project?._id) {
            const payload = {
                projectId: project._id,
                body: {
                    title: state.title,
                    description: state.description,
                    tag: state.tag,
                    project_type: state.project_type,
                    language: state.language,
                    category: state.category,
                    public_status: state.public_status,
                    disable_comments: state.disable_comments,
                    downloadable: state.downloadable,
                    ...(state.feature_image && { feature_image: state.feature_image }),
                },
            };
            reduxDispatch(updateProject(payload));
            dispatch({ type: 'SET_EDIT_MODE', value: false });
        } else {
            console.log('Setting edit mode to true');
            dispatch({ type: 'SET_EDIT_MODE', value: true });
        }
    };

    const handlePublish = () => {
        if (project?._id) {
            if (state.scheduled_published_date) {
                // If scheduled date is set, update the project with scheduled date
                const payload = {
                    projectId: project._id,
                    body: {
                        scheduled_published_date: state.scheduled_published_date,
                    },
                };
                reduxDispatch(updateProject(payload));
            } else {
                // If no scheduled date, publish immediately
                reduxDispatch(publishProject({ documentId: project._id, additionalPayload: true }));
            }
        }
    };

    const handleScheduledPublish = () => {
        if (project?._id && state.scheduled_published_date && state.scheduledPublishModified) {
            const payload = {
                projectId: project._id,
                body: {
                    scheduled_published_date: state.scheduled_published_date,
                },
            };
            reduxDispatch(updateProject(payload));
            // Reset the modified flag after triggering the update
            handleFormUpdate('scheduledPublishModified', false);
        }
    };

    const handleUserSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 3 && !searchingUser) {
                reduxDispatch(searchUser({ searchTerm, withProfileSearch: true }));
            }
        }, 1000),
    ).current;

    /** Get Project Details */
    const handleGetProject = useCallback(() => {
        reduxDispatch(connect());
        if (projectId)
            reduxDispatch(
                getManageProject({
                    id: projectId,
                }),
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleOnUploadCancel = () => {
        reduxDispatch(terminateUplaod());
    };

    const handleOnResumeUpload = () => {
        reduxDispatch(resumeUpload(undefined));
    };
    const handleOnPauseUplaod = () => {
        reduxDispatch(pauseUpload('Upload currently paused.'));
    };

    // Helper function to format scheduled date
    const formatScheduledDate = (date: Date | null) => {
        if (!date) return '';
        const now = new Date();
        const scheduledTime = new Date(date);
        const diffMs = scheduledTime.getTime() - now.getTime();
        const diffMins = Math.ceil(diffMs / (1000 * 60));

        if (diffMins <= 0) return 'Time elapsed';
        if (diffMins < 60) return `in ${diffMins} minutes`;
        if (diffMins < 1440) return `in ${Math.floor(diffMins / 60)} hours`;
        return `in ${Math.floor(diffMins / 1440)} days`;
    };

    useEffect(() => {
        handleGetProject();
    }, [handleGetProject]);

    useEffect(() => {
        if (project) {
            dispatch({
                type: 'RESET',
                payload: {
                    title: project.title || '',
                    description: project.description || '',
                    tag: project.tag || [],
                    project_type: project.project_type || '',
                    language: project.language || 'english',
                    category: project.category || '',
                    public_status: project.public_status || 'public',
                    disable_comments: project.disable_comments || false,
                    downloadable: project.downloadable !== false,
                    scheduled_published_date: project.scheduled_published_date || null,
                    feature_image_preview: project.feature_image || '',
                    credits: project.credits,
                    scheduledPublishModified: false, // Reset modified flag when loading project
                    isEditMode: false, // Explicitly set to false on initial load
                    project: project.project,
                    resolutions: project.resolutions,
                },
            });
            initialValues.current = project;
        }
    }, [project]);

    // Check if scheduled time has elapsed and auto-toggle off
    useEffect(() => {
        if (state.scheduled_published_date) {
            const checkScheduledTime = () => {
                const now = new Date();
                const scheduledTime = new Date(state.scheduled_published_date);

                if (scheduledTime <= now) {
                    // Scheduled time has elapsed, turn off scheduled publish
                    handleFormUpdate('scheduled_published_date', null);
                    handleFormUpdate('scheduledPublishModified', false);
                    // Show toast message
                    // Note: We can't use showToastMessage here as it's not available in the component
                    // The saga will handle the actual publishing when the scheduled time is reached
                }
            };

            // Check immediately
            checkScheduledTime();

            // Set up interval to check every minute
            const interval = setInterval(checkScheduledTime, 60000);

            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.scheduled_published_date]);

    // Force re-render every minute to update scheduled date display
    useEffect(() => {
        if (state.scheduled_published_date) {
            const interval = setInterval(() => {
                // Force re-render by updating a dummy state
                setError(prev => prev);
            }, 60000);

            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.scheduled_published_date]);

    if (loading) {
        return <Loading />;
    }

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                backgroundColor: theme[themeStyle].secondaryBg,
                '@keyframes pulse': {
                    '0%': {
                        opacity: 1,
                    },
                    '50%': {
                        opacity: 0.5,
                    },
                    '100%': {
                        opacity: 1,
                    },
                },
            }}>
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 2,
                }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mx="auto">
                    <Box display="flex" gap={2} alignItems="center">
                        <EsBackButton sx={{ color: theme[themeStyle].tag_white }} handleDetails={handleGoBack} />
                        <Box display="flex" gap={0.5} alignItems="center">
                            <ThspianFullLogo
                                width={'120px'}
                                height={'40px'}
                                fill={theme[themeStyle].logo_color}
                                onClick={handleLogoClick}
                                style={{ cursor: 'pointer' }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    textTransform: 'capitalize',
                                }}>
                                Workshop
                            </Typography>
                        </Box>
                    </Box>
                    {/* Desktop Buttons */}
                    <Box display={{ xs: 'none', md: 'flex' }} gap={1.5}>
                        <Button
                            variant="outlined"
                            startIcon={<Eye size={16} color={theme[themeStyle].tag_white} />}
                            sx={{ color: theme[themeStyle].tag_white, borderColor: theme[themeStyle].tag_white }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, textTransform: 'capitalize' }}>
                                Preview
                            </Typography>
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save size={16} color={theme[themeStyle].true_white} />}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSave();
                            }}
                            disabled={!state.isEditMode}
                            sx={{
                                bgcolor: state.isEditMode ? theme[themeStyle].main_blue : theme[themeStyle].darkGray60,
                                opacity: state.isEditMode ? 1 : 0.6,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textTransform: 'capitalize',
                                    color: theme[themeStyle].true_white,
                                }}>
                                {state.isEditMode ? 'Save Changes' : 'No Changes'}
                            </Typography>
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (state.scheduled_published_date && state.scheduledPublishModified) {
                                    handleScheduledPublish();
                                } else if (state.scheduled_published_date) {
                                    // Scheduled but not modified - do nothing
                                    return;
                                } else {
                                    handlePublish();
                                }
                            }}
                            disabled={isPublished}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, textTransform: 'capitalize' }}>
                                {isPublished
                                    ? 'Published'
                                    : state.scheduled_published_date
                                    ? state.scheduledPublishModified
                                        ? 'Update Schedule'
                                        : 'Scheduled'
                                    : 'Publish'}
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Box mx="auto" p={3} sx={{ pb: { xs: 20, md: 3 } }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        {/* Video Preview */}
                        <Card sx={{ bgcolor: theme[themeStyle].deep_grey_lighter }}>
                            <CardContent>
                                <Box position="relative" sx={{ backgroundColor: 'black', borderRadius: 2, mb: 2 }}>
                                    {/* Processing Status Banner */}
                                    {
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 10,
                                                left: 15,
                                                zIndex: 2,
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor:
                                                        project?.processing_complete || !replacingInProgress
                                                            ? theme[themeStyle].green_status_indicator_full
                                                            : theme[themeStyle].secondary_red,
                                                    animation: 'pulse 1.5s infinite',
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    letterSpacing: '0.5px',
                                                    color: theme[themeStyle].true_white,
                                                }}>
                                                {project?.processing_complete
                                                    ? 'Processing Complete'
                                                    : 'Processing Incomplete'}
                                            </Typography>
                                        </Box>
                                    }

                                    {isProjectProcessing && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%', // Fixed height for the video container
                                                backgroundColor: 'rgba(0,0,0,0.9)',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                zIndex: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 2,
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}>
                                            <Spinner color="white" height={30} width={30} />
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].true_white,
                                                    fontSize: 12,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}>
                                                Media processing...Please wait
                                            </Typography>
                                        </Box>
                                    )}
                                    {renderPlayer}
                                </Box>
                                {isProjectProcessing && (
                                    <ProgressBar
                                        progress={progress}
                                        onCancel={handleOnUploadCancel}
                                        uploadMessage={uploadMessage}
                                        onResume={handleOnResumeUpload}
                                        onPause={handleOnPauseUplaod}
                                        isPaused={isPaused}
                                        desktopSX={{
                                            marginTop: 0,
                                            mb: 2,
                                            marginLeft: 0,
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                        }}
                                        mobileSX={{
                                            mb: { xs: '10px', sm: '10px' },
                                            marginLeft: { xs: '10px', sm: '15px' },
                                            paddingLeft: { xs: '10px', sm: '40px' },
                                            paddingRight: { xs: '10px', sm: '16px' },
                                            overflowX: 'hidden',
                                        }}
                                    />
                                )}
                                {!isProjectProcessing && error && (
                                    <AppWarning
                                        message="There seems to be an issue with your project file. If this persists, feel free to reupload a new file."
                                        sx={{ border: `1px solid ${theme[themeStyle].primary_red}` }}
                                    />
                                )}
                                <Button
                                    disabled={isProjectProcessing || uploadLoading}
                                    variant="outlined"
                                    onClick={openFilePicker}
                                    fullWidth
                                    sx={{
                                        borderColor: theme[themeStyle].highlight,
                                    }}
                                    startIcon={<Upload size={16} color={theme[themeStyle].highlight} />}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 14,
                                            textTransform: 'capitalize',
                                        }}>
                                        Upload New Video File
                                    </Typography>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Video Details */}
                        <Card sx={{ mt: 3, bgcolor: theme[themeStyle].deep_grey_lighter }}>
                            <CardHeader
                                title="Details"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    },
                                }}
                            />
                            <CardContent>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={state.title}
                                    onChange={e => handleFormUpdate('title', e.target.value)}
                                    helperText={`${state.title.length}/100`}
                                    FormHelperTextProps={{
                                        sx: {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    margin="normal"
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={6}
                                    value={state.description}
                                    onChange={e => handleFormUpdate('description', e.target.value)}
                                    helperText={`${state.description.length}/5000`}
                                    FormHelperTextProps={{
                                        sx: {
                                            color: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    margin="normal"
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                        },
                                    }}
                                />
                                <Box mt={2}>
                                    <FormLabel
                                        sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        Tags
                                    </FormLabel>
                                    <Box display="flex" gap={1} flexWrap="wrap" my={1}>
                                        {state.tag.map((tag: string) => (
                                            <Chip
                                                key={tag}
                                                label={tag}
                                                onDelete={() => removeTag(tag)}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].black,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Box display="flex" gap={1}>
                                        <TextField
                                            value={newTag}
                                            onChange={e => setNewTag(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && addTag()}
                                            label="Add Tag"
                                            InputLabelProps={{
                                                sx: {
                                                    color: theme[themeStyle].grey,
                                                },
                                            }}
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: theme[themeStyle].grey,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme[themeStyle].highlight,
                                                    },
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme[themeStyle].primary_white,
                                                },
                                            }}
                                            InputProps={{
                                                style: {
                                                    background: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].lightWhite,
                                                    borderRadius: 8,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    border: `1px solid ${theme[themeStyle].form_gray}`,
                                                },
                                            }}
                                        />
                                        <IconButton onClick={addTag}>
                                            <Add sx={{ color: theme[themeStyle].tag_white }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Tabs */}
                        <Card sx={{ mt: 3, bgcolor: theme[themeStyle].deep_grey_lighter }}>
                            <CardHeader
                                title="Additional Settings"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    },
                                }}
                            />
                            <CardContent>
                                <MainTagCarousel
                                    list={['Thumbnail', 'Credits', 'Captions', 'End Screen']}
                                    disableTitle
                                    disableViewAll
                                    groupLayout
                                    preselect
                                    type="list"
                                    onSelectTag={(tag: string) => {
                                        setTab(tag.toLowerCase());
                                    }}
                                    style={{
                                        marginTop: '10px',
                                        overflowX: 'scroll',
                                        ...(!device.isMobile && { width: '100%' }),
                                    }}
                                    listStyle={{ ...(!device.isMobile && { width: '100%' }) }}
                                    tagStyle={{
                                        width: '100%',
                                    }}
                                />

                                {tab === 'thumbnail' && (
                                    <Box mt={2}>
                                        <PictureUploader
                                            initialImageSrc={state.feature_image_preview}
                                            onChange={handleImageCompressAndDispatch}
                                            onRemove={() => handleImageCompressAndDispatch(null)}
                                            aspect={project?.project_format === 'act' ? 0.5 : 1.7}
                                            sx={{ width: '100%' }}
                                        />
                                    </Box>
                                )}

                                {tab === 'credits' && (
                                    <Box mt={2}>
                                        {/* Display existing credits */}
                                        {state.credits &&
                                            !isEmpty(state.credits) &&
                                            state.credits.map((credit: Credit, index: number) => (
                                                <Box
                                                    key={index}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    p={2}
                                                    mb={1}
                                                    sx={{
                                                        backgroundColor: theme[themeStyle].form_black,
                                                        borderRadius: 2,
                                                        border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    }}>
                                                    <Box display="flex" alignItems="center" gap={2}>
                                                        <ThspianAvatar
                                                            user={credit.user}
                                                            sx={{ width: 40, height: 40 }}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontSize: 14,
                                                                }}>
                                                                {credit.credit_role || 'No Role'}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    color: theme[themeStyle].lightWhite,
                                                                    fontSize: 12,
                                                                }}>
                                                                {credit.user?.firstName && credit.user?.lastName
                                                                    ? `${credit.user.firstName} ${credit.user.lastName}`
                                                                    : 'No User Selected'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        onClick={() => handleRemoveCredit(index)}
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            '&:hover': {
                                                                color: theme[themeStyle].primary_orange,
                                                            },
                                                        }}>
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}

                                        {/* Add new credit form */}
                                        {showAddCredit && (
                                            <Box
                                                p={2}
                                                sx={{
                                                    backgroundColor: theme[themeStyle].form_black,
                                                    borderRadius: 2,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                }}>
                                                <Box display="flex" gap={1} alignItems="center" mb={2}>
                                                    <Autocomplete
                                                        options={CREATIVE_ROLES}
                                                        freeSolo
                                                        filterOptions={(options, { inputValue }) => {
                                                            const filtered = options.filter(option =>
                                                                option.toLowerCase().includes(inputValue.toLowerCase()),
                                                            );
                                                            return filtered;
                                                        }}
                                                        value={
                                                            state.credits[state.credits.length - 1]?.credit_role || ''
                                                        }
                                                        onChange={(_, newValue) => {
                                                            updateCredit(
                                                                state.credits.length - 1,
                                                                'credit_role',
                                                                typeof newValue === 'string' ? newValue : '',
                                                            );
                                                        }}
                                                        onInputChange={(_, newInputValue) => {
                                                            updateCredit(
                                                                state.credits.length - 1,
                                                                'credit_role',
                                                                newInputValue,
                                                            );
                                                        }}
                                                        getOptionLabel={option => option}
                                                        slotProps={{
                                                            popper: {
                                                                modifiers: [
                                                                    {
                                                                        name: 'offset',
                                                                        options: {
                                                                            offset: [0, 0], // no vertical spacing between input and dropdown
                                                                        },
                                                                    },
                                                                ],
                                                            },
                                                            paper: {
                                                                sx: {
                                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                                    borderRadius: '8px',
                                                                    maxHeight: 300,
                                                                    margin: 0, // removes margin between paper and input
                                                                },
                                                            },
                                                        }}
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                placeholder="Search and Select Role"
                                                                label="Role"
                                                                InputLabelProps={{
                                                                    sx: {
                                                                        color: theme[themeStyle].grey,
                                                                    },
                                                                }}
                                                                fullWidth
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: theme[themeStyle].grey,
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: theme[themeStyle].highlight,
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: theme[themeStyle].highlight,
                                                                        },
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: theme[themeStyle].primary_white,
                                                                    },
                                                                }}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    style: {
                                                                        background: theme[themeStyle].gray_text,
                                                                        color: theme[themeStyle].lightWhite,
                                                                        borderRadius: 8,
                                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                        border: `1px solid ${theme[themeStyle].form_gray}`,
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        renderOption={(props, option, { selected, index }) => (
                                                            <li
                                                                {...props}
                                                                key={`${option}-${props.id}-${index}`}
                                                                style={{
                                                                    backgroundColor: selected
                                                                        ? theme[themeStyle].highlight
                                                                        : theme[themeStyle].dropdown_black,
                                                                    cursor: 'pointer',
                                                                    padding: 0,
                                                                    margin: 0,
                                                                    borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                                                }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        width: '100%',
                                                                        py: 1,
                                                                        px: 1.5,
                                                                        backgroundColor: selected
                                                                            ? theme[themeStyle].highlight
                                                                            : 'transparent',
                                                                        '&:hover': {
                                                                            backgroundColor:
                                                                                theme[themeStyle].form_divider,
                                                                        },
                                                                    }}>
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                            color: theme[themeStyle].tag_white,
                                                                        }}>
                                                                        {option}
                                                                    </Typography>
                                                                </Box>
                                                            </li>
                                                        )}
                                                        sx={{
                                                            '& .MuiAutocomplete-paper': {
                                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                                borderRadius: '8px',
                                                                maxHeight: 300,
                                                            },
                                                            '& .MuiAutocomplete-option': {
                                                                color: theme[themeStyle].primary_white,
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                padding: 2,
                                                                '&:hover': {
                                                                    backgroundColor: theme[themeStyle].form_divider,
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: theme[themeStyle].highlight,
                                                                },
                                                            },
                                                            width: '100%',
                                                        }}
                                                    />
                                                </Box>
                                                <Autocomplete
                                                    options={userResults}
                                                    slotProps={{
                                                        popper: {
                                                            modifiers: [
                                                                {
                                                                    name: 'offset',
                                                                    options: {
                                                                        offset: [0, 0], // no vertical spacing between input and dropdown
                                                                    },
                                                                },
                                                            ],
                                                        },
                                                        paper: {
                                                            sx: {
                                                                backgroundColor: theme[themeStyle].dropdown_black,
                                                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                                                borderRadius: '8px',
                                                                maxHeight: 300,
                                                                margin: 0, // removes margin between paper and input
                                                            },
                                                        },
                                                    }}
                                                    getOptionLabel={option => {
                                                        if (typeof option === 'string') return option;
                                                        return `${option.firstName} ${option.lastName}`;
                                                    }}
                                                    value={
                                                        state.credits[state.credits.length - 1]?.user?._id
                                                            ? state.credits[state.credits.length - 1].user
                                                            : null
                                                    }
                                                    onChange={(_, newValue) => {
                                                        updateCredit(
                                                            state.credits.length - 1,
                                                            'user',
                                                            newValue || ({} as UserOption),
                                                        );
                                                    }}
                                                    onInputChange={(_, newInputValue) => {
                                                        handleUserSearch(newInputValue);
                                                    }}
                                                    freeSolo
                                                    selectOnFocus
                                                    clearOnBlur
                                                    handleHomeEndKeys
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Search and Select User"
                                                            label="Search and Select User"
                                                            InputLabelProps={{
                                                                sx: {
                                                                    color: theme[themeStyle].grey,
                                                                },
                                                            }}
                                                            sx={{
                                                                width: '100%',
                                                                '& .MuiOutlinedInput-root': {
                                                                    '& fieldset': {
                                                                        borderColor: theme[themeStyle].grey,
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: theme[themeStyle].highlight,
                                                                    },
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    color: theme[themeStyle].primary_white,
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: theme[themeStyle].lightWhite,
                                                                },
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                style: {
                                                                    background: theme[themeStyle].gray_text,
                                                                    color: theme[themeStyle].lightWhite,
                                                                    borderRadius: 8,
                                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                    border: `1px solid ${theme[themeStyle].form_gray}`,
                                                                },
                                                                endAdornment: (
                                                                    <>
                                                                        {searchingUser ? (
                                                                            <CircularProgress
                                                                                color="inherit"
                                                                                size={20}
                                                                            />
                                                                        ) : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                    renderOption={(props, option, { selected, index }) => (
                                                        <li
                                                            {...props}
                                                            key={`${option._id}-${index}`}
                                                            style={{
                                                                backgroundColor: selected
                                                                    ? theme[themeStyle].highlight
                                                                    : theme[themeStyle].dropdown_black,
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                margin: 0,
                                                                borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                                                            }}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    width: '100%',
                                                                    py: 1,
                                                                    px: 1.5,
                                                                    backgroundColor: selected
                                                                        ? theme[themeStyle].highlight
                                                                        : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: theme[themeStyle].form_divider,
                                                                    },
                                                                }}>
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                    mb={1}>
                                                                    <ThspianAvatar
                                                                        user={option}
                                                                        sx={{ width: 30, height: 30 }}
                                                                    />
                                                                    <Typography
                                                                        sx={{ color: theme[themeStyle].tag_white }}>
                                                                        {option.firstName} {option.lastName}
                                                                    </Typography>
                                                                </Stack>

                                                                {option.profiles && option.profiles.length > 0 && (
                                                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                                                        {option.profiles.map(
                                                                            (profile: IWorkProfileSummary) => (
                                                                                <Stack
                                                                                    direction="row"
                                                                                    key={profile._id}
                                                                                    alignItems="center"
                                                                                    gap={1}>
                                                                                    <Box
                                                                                        onClick={e => {
                                                                                            e.stopPropagation(); // Prevent event bubbling

                                                                                            const updatedUser = {
                                                                                                ...option,
                                                                                                selectedProfile:
                                                                                                    profile,
                                                                                            };

                                                                                            updateCredit(
                                                                                                state.credits.length -
                                                                                                    1,
                                                                                                'user',
                                                                                                updatedUser,
                                                                                            );
                                                                                        }}
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                            borderRadius: '50%',
                                                                                            overflow: 'hidden',
                                                                                            border: `2px solid ${theme[themeStyle].form_divider}`,
                                                                                            '&:hover': {
                                                                                                borderColor:
                                                                                                    theme[themeStyle]
                                                                                                        .highlight,
                                                                                            },
                                                                                        }}>
                                                                                        <img
                                                                                            src={
                                                                                                profile.profile_picture ||
                                                                                                profile.feature_picture
                                                                                            }
                                                                                            alt={profile.stage_name}
                                                                                            style={{
                                                                                                width: 24,
                                                                                                height: 24,
                                                                                                objectFit: 'cover',
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontFamily:
                                                                                                FONT_ENUM.MAIN_MEDIUM,
                                                                                            fontSize: 8,
                                                                                            color: theme[themeStyle]
                                                                                                .tag_white,
                                                                                            textTransform: 'capitalize',
                                                                                        }}>
                                                                                        {profile.profile_type}
                                                                                    </Typography>
                                                                                </Stack>
                                                                            ),
                                                                        )}
                                                                    </Stack>
                                                                )}
                                                            </Box>
                                                        </li>
                                                    )}
                                                    sx={{
                                                        '& .MuiAutocomplete-paper': {
                                                            backgroundColor: theme[themeStyle].dropdown_black,
                                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                                            borderRadius: '8px',
                                                            maxHeight: 300,
                                                        },
                                                        '& .MuiAutocomplete-option': {
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            padding: 2,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].form_divider,
                                                            },
                                                            '&.Mui-focused': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                        width: '100%',
                                                    }}
                                                />
                                                <Box display="flex" gap={1} mt={2}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => {
                                                            // Remove the unsaved credit and hide the form
                                                            removeCredit(state.credits.length - 1);
                                                            setShowAddCredit(false);
                                                        }}
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            borderColor: theme[themeStyle].tag_white,
                                                            '&:hover': {
                                                                borderColor: theme[themeStyle].highlight,
                                                                color: theme[themeStyle].highlight,
                                                            },
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSaveCredit}
                                                        sx={{
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        }}>
                                                        Save Credit
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Add new credit button */}
                                        {!showAddCredit && !hasUnsavedCredits() && (
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<Add />}
                                                onClick={() => {
                                                    addCredit();
                                                    setShowAddCredit(true);
                                                }}
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    borderColor: theme[themeStyle].tag_white,
                                                    '&:hover': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                }}>
                                                Add Credit
                                            </Button>
                                        )}
                                    </Box>
                                )}

                                {tab === 'captions' && (
                                    <Box mt={2}>
                                        <EmptySection
                                            header="Project Captions"
                                            subHeader="Project Caption Tool Coming Soon"
                                            message="We're working on advanced tooling for your project. Soon you'll be able to upload captions, and more in this section."
                                        />
                                    </Box>
                                )}

                                {tab === 'end screen' && (
                                    <Box mt={2}>
                                        <EmptySection
                                            header="Custom End Screens"
                                            subHeader="Customize your end screens"
                                            message="We're working on new ways to end your story. Soon you'll be able create custom end screens, and more in this section."
                                        />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Sidebar */}
                    <Grid item xs={12} md={4}>
                        <WorkshopInstagramAutomationCard
                            hasMedia={Boolean(standaloneMedia?.media_url)}
                            automationActive={igAutomationActive}
                            onOpenModal={() => setAutomationModalOpen(true)}
                        />

                        <Card sx={{ mt: 2, bgcolor: theme[themeStyle].deep_grey_lighter, p: 2 }}>
                            <CardHeader
                                title="Visibility"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    },
                                }}
                            />
                            <CardContent>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        value={state.public_status}
                                        onChange={e =>
                                            handleFormUpdate(
                                                'public_status',
                                                e.target.value as 'public' | 'unlisted' | 'private',
                                            )
                                        }>
                                        <FormControlLabel
                                            value="public"
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        '&.Mui-checked': {
                                                            color: theme[themeStyle].highlight,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Globe size={16} color={theme[themeStyle].tag_white} />
                                                    <Stack>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 16,
                                                                color: theme[themeStyle].tag_white,
                                                            }}>
                                                            Public
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 10,
                                                                color: theme[themeStyle].lightWhite,
                                                                opacity: 0.7,
                                                            }}>
                                                            Your project will be visible to the public and in searches
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            value="unlisted"
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        '&.Mui-checked': {
                                                            color: theme[themeStyle].highlight,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Users size={16} color={theme[themeStyle].tag_white} />
                                                    <Stack>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 16,
                                                                color: theme[themeStyle].tag_white,
                                                            }}>
                                                            Unlisted
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 10,
                                                                color: theme[themeStyle].lightWhite,
                                                                opacity: 0.7,
                                                            }}>
                                                            Your project will be invisible to public search, but visible
                                                            to people you share your link with.
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            }
                                        />
                                        <FormControlLabel
                                            value="private"
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        '&.Mui-checked': {
                                                            color: theme[themeStyle].highlight,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Lock size={16} color={theme[themeStyle].tag_white} />
                                                    <Stack>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 16,
                                                                color: theme[themeStyle].tag_white,
                                                            }}>
                                                            Private
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                fontSize: 10,
                                                                color: theme[themeStyle].lightWhite,
                                                                opacity: 0.7,
                                                            }}>
                                                            This project will only be visible to you and submitted
                                                            campaign hosts.
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>
                                <Divider sx={{ my: 2 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <FormLabel
                                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white }}>
                                        Schedule for later
                                    </FormLabel>
                                    <CustomizedSwitches
                                        switchProps={{
                                            checked: !!state.scheduled_published_date,
                                            disabled: isPublished,
                                            onChange: e => {
                                                if (e.target.checked) {
                                                    // Set scheduled date to 10 minutes from now
                                                    const futureDate = new Date();
                                                    futureDate.setMinutes(futureDate.getMinutes() + 10);
                                                    handleFormUpdate('scheduled_published_date', futureDate);
                                                    handleFormUpdate('scheduledPublishModified', true);
                                                } else {
                                                    handleFormUpdate('scheduled_published_date', null);
                                                    handleFormUpdate('scheduledPublishModified', true);
                                                }
                                            },
                                        }}
                                        label=""
                                        style={{ color: theme[themeStyle].tag_white }}
                                    />
                                </Box>
                                {state.scheduled_published_date && (
                                    <Box mt={2} display="flex" flexDirection="column" gap={2}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: state.scheduledPublishModified
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 12,
                                            }}>
                                            {state.scheduledPublishModified ? 'Modified: ' : ''}Scheduled to publish{' '}
                                            {formatScheduledDate(state.scheduled_published_date)}
                                        </Typography>
                                        <CustomDatePicker
                                            value={state.scheduled_published_date}
                                            onChange={date => {
                                                if (date) {
                                                    const selectedDate = new Date(date);
                                                    const now = new Date();
                                                    const minDate = new Date(now.getTime() + 10 * 60 * 1000); // 10 minutes from now

                                                    if (selectedDate < minDate) {
                                                        // If selected date is less than 10 minutes from now, set it to 10 minutes from now
                                                        const futureDate = new Date();
                                                        futureDate.setMinutes(futureDate.getMinutes() + 10);
                                                        handleFormUpdate('scheduled_published_date', futureDate);
                                                        handleFormUpdate('scheduledPublishModified', true);
                                                    } else {
                                                        handleFormUpdate('scheduled_published_date', selectedDate);
                                                        handleFormUpdate('scheduledPublishModified', true);
                                                    }
                                                }
                                            }}
                                            minDate={dayjs().add(10, 'minute')}
                                            withTime={true}
                                            withTimezone={false}
                                            disabled={isPublished}
                                            sx={{
                                                backgroundColor: theme[themeStyle].gray_text,
                                                '&:focus-within': {
                                                    border: `1px solid ${theme[themeStyle].highlight}`,
                                                },
                                            }}
                                        />
                                        {isPublished && (
                                            <AppWarning
                                                message="This area is disabled because the project is published."
                                                sx={{ border: `1px solid ${theme[themeStyle].darkGray60}`, mb: 0 }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                        {project && (
                            <Card sx={{ mt: 2, bgcolor: theme[themeStyle].deep_grey_lighter, p: 2 }}>
                                <LinkBox link={generateShareLink('project', project as unknown as IProject, true)} />
                            </Card>
                        )}

                        <Card sx={{ mt: 2, bgcolor: theme[themeStyle].deep_grey_lighter, p: 2 }}>
                            <CardHeader
                                title="More Options"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    },
                                }}
                            />
                            <CardContent>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Category</InputLabel>
                                    <Select
                                        value={state.category}
                                        onChange={e => handleFormUpdate('category', e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: '8px',
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}>
                                        {PROJECT_CATEGORY.map(category => (
                                            <MenuItem key={category} value={category}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Language</InputLabel>
                                    <Select
                                        value={state.language}
                                        onChange={e => handleFormUpdate('language', e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].dropdown_black,
                                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                                    borderRadius: '8px',
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].form_divider,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            color: theme[themeStyle].primary_white,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].highlight,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                color: theme[themeStyle].tag_white,
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].tag_white,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme[themeStyle].highlight,
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: theme[themeStyle].primary_white,
                                            },
                                        }}>
                                        {LANGUAGES.map(language => (
                                            <MenuItem key={language} value={language}>
                                                {language}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                    <FormLabel
                                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white }}>
                                        Allow comments
                                    </FormLabel>
                                    <CustomizedSwitches
                                        switchProps={{
                                            checked: !state.disable_comments,
                                            onChange: e => handleFormUpdate('disable_comments', !e.target.checked),
                                        }}
                                        label=""
                                        style={{ color: theme[themeStyle].tag_white }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                    <FormLabel
                                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white }}>
                                        Allow downloads
                                    </FormLabel>
                                    <CustomizedSwitches
                                        switchProps={{
                                            checked: state.downloadable,
                                            onChange: e => handleFormUpdate('downloadable', e.target.checked),
                                        }}
                                        label=""
                                        style={{ color: theme[themeStyle].tag_white }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ mt: 3, bgcolor: theme[themeStyle].deep_grey_lighter, p: 2 }}>
                            <CardHeader
                                title="Analytics Preview"
                                sx={{ color: theme[themeStyle].tag_white }}
                                titleTypographyProps={{
                                    sx: {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    },
                                }}
                            />
                            <CardContent>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ color: theme[themeStyle].tag_white, mb: 2 }}>
                                    <span style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16 }}>
                                        Estimated reach:
                                    </span>
                                    <strong style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                        {formatNumberWithK(project?.view_count!)} views
                                    </strong>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ color: theme[themeStyle].tag_white, mb: 2 }}>
                                    <span style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16 }}>
                                        Watch hours:
                                    </span>
                                    <strong style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                        {(project?.total_view_hours! / 60)?.toFixed(2) || '0.00'}
                                    </strong>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ color: theme[themeStyle].tag_white, mb: 2 }}>
                                    <span style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16 }}>
                                        Total comments:
                                    </span>
                                    <strong style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                        {formatNumberWithK(project?.total_comments!)}
                                    </strong>
                                </Typography>

                                <Typography
                                    variant="body2"
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ color: theme[themeStyle].tag_white, mb: 2 }}>
                                    <span style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16 }}>
                                        Total Submissions:
                                    </span>
                                    <strong style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                        {project?.total_submissions || 0}
                                    </strong>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    display="flex"
                                    justifyContent="space-between"
                                    sx={{ color: theme[themeStyle].tag_white, mb: 2 }}>
                                    <span style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16 }}>
                                        Total Votes:
                                    </span>
                                    <strong style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                        {project?.total_votes || 0}
                                    </strong>
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card sx={{ mt: 3, bgcolor: theme[themeStyle].deep_grey_lighter, p: 2 }}>
                            <CardHeader
                                title={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>Revenue</Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 10,
                                                color: theme[themeStyle].primary_orange,
                                                backgroundColor: 'rgba(255, 165, 0, 0.1)',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                textTransform: 'uppercase',
                                            }}>
                                            Beta
                                        </Typography>
                                    </Box>
                                }
                                sx={{ color: theme[themeStyle].tag_white }}
                            />
                            <CardContent>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 32,
                                        color: theme[themeStyle].highlight,
                                        textAlign: 'center',
                                        mb: 1,
                                    }}>
                                    ${project?.revenue?.total_usd?.toFixed(2) || '0.00'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Mobile Action Buttons */}
            <Box
                display={{ xs: 'flex', md: 'none' }}
                position="fixed"
                bottom={0}
                left={0}
                right={0}
                zIndex={1000}
                sx={{
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    borderTop: 1,
                    borderColor: 'divider',
                    padding: 2,
                    flexDirection: 'column',
                    gap: 1,
                }}>
                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Eye size={16} color={theme[themeStyle].tag_white} />}
                    sx={{
                        color: theme[themeStyle].tag_white,
                        borderColor: theme[themeStyle].tag_white,
                        py: 1.5,
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, textTransform: 'capitalize' }}>
                        Preview
                    </Typography>
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<Save size={16} color={theme[themeStyle].true_white} />}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSave();
                    }}
                    disabled={!state.isEditMode}
                    sx={{
                        bgcolor: state.isEditMode ? theme[themeStyle].main_blue : theme[themeStyle].darkGray60,
                        opacity: state.isEditMode ? 1 : 0.6,
                        py: 1.5,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            textTransform: 'capitalize',
                            color: theme[themeStyle].true_white,
                        }}>
                        {state.isEditMode ? 'Save Changes' : 'No Changes'}
                    </Typography>
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (state.scheduled_published_date && state.scheduledPublishModified) {
                            handleScheduledPublish();
                        } else if (state.scheduled_published_date) {
                            // Scheduled but not modified - do nothing
                            return;
                        } else {
                            handlePublish();
                        }
                    }}
                    disabled={isPublished}
                    sx={{ py: 1.5 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, textTransform: 'capitalize' }}>
                        {isPublished
                            ? 'Published'
                            : state.scheduled_published_date
                            ? state.scheduledPublishModified
                                ? 'Update Schedule'
                                : 'Scheduled'
                            : 'Publish'}
                    </Typography>
                </Button>
            </Box>

            <InstagramPostAutomationModal
                open={automationModalOpen}
                onClose={() => setAutomationModalOpen(false)}
                standaloneMedia={standaloneMedia ?? undefined}
                projectTitle={state.title || project?.title}
                defaultCaption={state.description ? `${state.title}\n\n${state.description}`.trim() : state.title}
                onAutomationStatusChange={setIgAutomationActive}
                initialDraft={getPostAutomationDraft()}
                autoPublishWhenReady={shouldAutoPublishPostAutomation(projectId)}
            />

            {/* Workshop is outside AppLayout — mount upgrade modal here so it actually appears */}
            <AccountSubscriptionModal
                open={subscriptionModalOpen}
                onClose={() => reduxDispatch(toggleSubscriptionModal(false))}
            />

            {/* Delete Credit Confirmation Dialog */}
            <DialogueSheet
                open={deleteCreditIndex !== null}
                onClose={() => setDeleteCreditIndex(null)}
                onAgreeClicked={handleDeleteCredit}
                onDisagreeClick={() => setDeleteCreditIndex(null)}
                header="Delete Credit"
                body="Are you sure you want to delete this credit? This action cannot be undone."
                positiveText="Delete"
                negativeText="Cancel"
                positiveTextStyle={{
                    color: theme[themeStyle].primary_orange,
                }}
            />
        </Box>
    );
};

export default WorkShopComponent;
